// Inter variable font embedded as a data: URL so the print iframe never
// touches the network at print time. See ./interFont.js for the encoded blob.
// eslint-disable-next-line import/extensions
import interFontDataUrl from './interFont';

/* ── Monochrome line icons (Feather, MIT) ───────────────────────────────────
   Inline SVG strings using `currentColor` so they inherit the surrounding
   text color. Sized by the CSS class .icon-svg → small, crisp, no emoji. */
const ICONS = {
  user: '<svg class="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
  pin: '<svg class="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>',
  calendar: '<svg class="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>',
  box: '<svg class="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>',
};

const createIframe = () => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'absolute';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  return iframe;
};

export default function packageSticker(data) {
  const iframe = createIframe();

  // ── Derive header fields from the products list ─────────────────────────
  // Not every product line carries the customer info (e.g. local-replacement
  // lines don't have webUser / deliveryMethod). Scan the list to find the
  // first line that does, while still using products[0] for the order header.
  const products = data.products || [];
  const first = products[0] || {};
  const metaData = (first.metaData || '').replace(/[<>"']/g, '');

  const withUser = products.find((p) => p && p.webUser && p.webUser.name) || {};
  const withDelivery = products.find((p) => p && p.deliveryMethod && p.deliveryMethod.name) || {};

  // Customer name: from webUser when available, otherwise omit (workshop / B2B orders)
  const customerName = withUser.webUser?.name || '';

  // City: parse from deliveryMethod.name (same logic as ProcessedOrders.vue → cityFromDelivery)
  let city = '';
  const deliveryName = withDelivery.deliveryMethod?.name || '';
  if (deliveryName) {
    if (deliveryName.startsWith('Postnord')) {
      city = 'POSTNORD';
    } else {
      const match = deliveryName.match(/(?<=\d{4} )[^/]+(?= \/)/);
      city = match ? match[0] : '';
    }
  }

  // Date — prefer packedAt > processedAt > addedAt
  const dateSource = first.packedAt || first.processedAt || first.addedAt;
  const d = dateSource ? new Date(dateSource) : new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const dateStr = `${dd}.${mm}.${yyyy}`;

  // Dynamic order-number font size. The order-no now spans the full sticker
  // width (below the top row), so we can be more generous than before while
  // still keeping very long custom labels on a single line.
  const metaLen = metaData.length;
  let orderFontSize = 40;
  if (metaLen > 16 && metaLen <= 24) orderFontSize = 34;
  else if (metaLen > 24 && metaLen <= 34) orderFontSize = 26;
  else if (metaLen > 34) orderFontSize = 20;

  // Populate the iframe's document via the standard open/write/close API.
  // (Note: `document.write` is only discouraged for the top-level document
  // after parsing — writing into a freshly opened iframe document is the
  // intended, spec-compliant use of this API and its `load` event fires
  // reliably after `close()`, which the callers depend on to trigger print.)
  const doc = iframe.contentDocument;
  doc.open();
  doc.write(`
    <html lang="no">
      <head>
        <title>Kundeetikett / Ordre</title>
        <style>
          /* Inter variable font, embedded as a base64 data URL so the print
             iframe needs no network access at print time. */
          @font-face {
            font-family: 'Inter';
            font-style: normal;
            font-weight: 100 900;
            font-display: block;
            src: url(${interFontDataUrl}) format('woff2-variations'),
                 url(${interFontDataUrl}) format('woff2');
          }

          @page {
            size: 102mm 192mm;
            margin: 0;
          }

          * { box-sizing: border-box; }

          body {
            margin: 0;
            padding: 0;
            width: 102mm;
            background: #ffffff;
            font-family: 'Inter', 'Helvetica Neue', 'Arial Narrow', Arial, sans-serif;
            color: #000;
            -webkit-font-smoothing: antialiased;
          }

          .sheet {
            width: 100%;
            min-height: 192mm;
            padding: 10px 14px;
            border: 2px solid #000;
            border-radius: 14px;
            display: flex;
            flex-direction: column;
          }

          /* Top: brand + QR */
          .top {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }

          .brand {
            flex: 1;
            min-width: 0;
          }

          .brand-title {
            margin: 0;
            font-size: 22px;
            font-weight: 800;
            letter-spacing: 0.5px;
          }

          .brand-sub {
            margin: 2px 0 0 0;
            font-size: 10px;
            letter-spacing: 1.2px;
            color: #222;
          }

          .brand-rule {
            border: none;
            border-top: 1.5px solid #000;
            margin: 6px 0 8px 0;
          }

          .qr-wrap {
            text-align: center;
            margin-left: 10px;
          }

          .qr-box {
            border: 1.5px solid #000;
            border-radius: 6px;
            padding: 3px;
            display: inline-block;
          }

          .qr-box img { display: block; width: 130px; height: 130px; }

          /* Order number — now on its own full-width row below the top block,
             so long metaData strings don't wrap awkwardly next to the QR. */
          .order-row {
            margin-top: 8px;
            padding: 6px 10px;
            border: 1.5px solid #000;
            border-radius: 8px;
            text-align: center;
          }

          .order-no {
            font-weight: 800;
            margin: 0;
            line-height: 1.05;
            letter-spacing: 1px;
            word-break: break-word;
            overflow-wrap: anywhere;
          }

          .customer {
            margin-top: 6px;
            display: flex;
            flex-direction: column;
            gap: 2px;
            font-size: 13px;
            font-weight: 700;
          }

          .customer .line { display: flex; align-items: center; gap: 8px; }
          .customer .name,
          .customer .city { text-transform: uppercase; }
          .customer .name { font-size: 18px; line-height: 1.05; }
          .customer .city { font-size: 26px; line-height: 1.05; }
          .customer .date { font-size: 18px; font-weight: 700; line-height: 1.05; }
          .customer .line .icon-svg {
            width: 22px;
            height: 22px;
            flex: 0 0 22px;
            vertical-align: -4px;
          }

          /* Monochrome line icons. They inherit color from surrounding text. */
          .icon-svg {
            width: 13px;
            height: 13px;
            flex: 0 0 13px;
            display: inline-block;
            vertical-align: -2px;
          }

          .info-label .icon-svg {
            width: 10px;
            height: 10px;
            flex: 0 0 10px;
            vertical-align: -1px;
            margin-right: 3px;
          }

          /* Contents */
          .contents-tab {
            margin-top: 10px;
            margin-bottom: 0;
            display: inline-block;
            background: #000;
            color: #fff;
            padding: 3px 14px;
            font-size: 10px;
            font-weight: 800;
            letter-spacing: 1.5px;
            border-radius: 5px 5px 0 0;
          }

          .contents {
            margin-top: 0;
            border: 1px solid #000;
            border-radius: 0 0 10px 10px;
            overflow: hidden;
            display: block;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }

          thead th {
            background: #f0f0f0;
            border-bottom: 1px solid #000;
            text-align: left;
            padding: 5px 10px;
            font-size: 9px;
            letter-spacing: 0.8px;
            color: #333;
          }

          thead th.num { width: 8%; }
          thead th.qty { width: 16%; text-align: right; }
          thead th.price { width: 18%; text-align: right; }

          tbody td {
            padding: 5px 10px;
            border-bottom: 1px dotted #bbb;
            vertical-align: middle;
            line-height: 1.25;
          }

          tbody tr:last-child td { border-bottom: none; }

          td.num { font-weight: 700; }
          td.qty { text-align: right; font-weight: 700; }
          td.price { text-align: right; font-weight: 700; }

          .local-badge {
            display: inline-block;
            background: #000;
            color: #fff;
            padding: 1px 5px;
            border-radius: 3px;
            font-size: 8px;
            font-weight: 700;
            margin-left: 4px;
            vertical-align: middle;
          }

          /* ── Multi-page printing ────────────────────────────────────────────
             If the items list is longer than what fits on a single 6×4 label,
             the browser will paginate the table automatically. These rules
             keep individual rows intact and repeat the column headings on
             every printed page (browsers natively repeat <thead> across pages
             when the table breaks). */
          tbody tr { page-break-inside: avoid; break-inside: avoid; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          .top, .contents-tab, .order-row { page-break-after: avoid; break-after: avoid; }
        </style>
      </head>
      <body>
        <div class="sheet">
          <div class="top">
            <div class="brand">
              <h1 class="brand-title">BD BILDELER</h1>
              <p class="brand-sub">KUNDEETIKETT / ORDRE</p>
              <hr class="brand-rule" />
              <div class="customer">
                ${city ? `<div class="line city-line">${ICONS.pin}<span class="city">${city}</span></div>` : ''}
                ${customerName ? `<div class="line">${ICONS.user}<span class="name">${customerName}</span></div>` : ''}
                <div class="line date-line">${ICONS.calendar}<span class="date">${dateStr}</span></div>
              </div>
            </div>
            <div class="qr-wrap">
              <div class="qr-box">
                <img src="data:image/png;base64,${data.code}" alt="QR" />
              </div>
            </div>
          </div>

          <div class="order-row">
            <p class="order-no" style="font-size:${orderFontSize}px;">${metaData}</p>
          </div>

          <span class="contents-tab">PAKKEINNHOLD</span>
          <div class="contents">
            <table>
              <thead>
                <tr>
                  <th class="num">NR.</th>
                  <th>VAREBESKRIVELSE</th>
                  <th class="qty">ANTALL</th>
                  <th class="price">PRIS</th>
                </tr>
              </thead>
              <tbody id="tableBody"></tbody>
            </table>
          </div>
        </div>
        <script>
          (function () {
            const products = ${JSON.stringify(products)};
            const tbody = document.getElementById('tableBody');
            products.forEach((item, idx) => {
              const tr = document.createElement('tr');

              const num = document.createElement('td');
              num.className = 'num';
              num.textContent = idx + 1;

              const name = document.createElement('td');
              let label = item.noteType === 3 && item.notes
                ? item.name + ' (' + String(item.notes).replace(/<[^>]*>|&nbsp;/g, '') + ')'
                : item.name;
              if (item && item.replacedWithLocal) {
                label += '<span class="local-badge">LOCAL</span>';
              }
              name.innerHTML = (label || '').toUpperCase();

              const qty = document.createElement('td');
              qty.className = 'qty';
              qty.textContent = (item.count || 0) + ' STK';

              const price = document.createElement('td');
              price.className = 'price';
              price.textContent = (item.webPrice * item.count) || ' ';

              tr.appendChild(num);
              tr.appendChild(name);
              tr.appendChild(qty);
              tr.appendChild(price);
              tbody.appendChild(tr);
            });
          })();
        </script>
      </body>
    </html>
  `);
  doc.close();

  return iframe;
}
